// Line of sight on the board's squares (issue 1144, slice 2; docs/VTT/design/30-sight-walls-and-light.md §1.6).
//
// THE SERVER'S RULE: a square is seen from an eye when the straight line from the eye to the square's CENTRE touches
// no wall that blocks sight (a closed door always blocks; an open one never). Touching counts as blocked: a line that
// grazes a wall's end, or runs through the corner where two walls meet, is stopped — when the rule is unsure it errs on
// the secret's side (`segmentsMeet`, the movement rule's own test). The line walks the squares it passes through
// (Amanatides–Woo, over the fitted grid's uneven lines) and tests only the walls listed in those squares; the walls are
// listed by square once, and again when a wall, door or the grid changes. Measured in design/30 §7 (rows B against F):
// eight times cheaper on the server than visibility polygons, which are what a SCREEN needs to draw shadow (§1.7).
//
// Pure, in board pixels and grid indices (`axisPositions`: the board's edges are the outer lines), so the server and a
// test run the same code. Light (#1145) is `litFrom`: a light reaches a square by the same line rule. Darkvision
// and blindsight (#1146, `sight.ts`) are ranges an eye sees within, by the same line rule.
import { axisPositions } from '../grid/cells.js';
import { blocksSight, segmentsMeet } from '../grid/walls.js';
export function sightGrid(grid, extent) {
    const xs = axisPositions(grid, extent, 'x');
    const ys = axisPositions(grid, extent, 'y');
    return { xs, ys, cols: Math.max(0, xs.length - 1), rows: Math.max(0, ys.length - 1) };
}
/** The cell a coordinate falls in on one axis, clamped to the board (a binary search over the lines). */
export function lineCellOf(lines, v) {
    const last = lines.length - 2;
    if (last <= 0)
        return 0;
    let lo = 0;
    let hi = last;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (v >= lines[mid])
            lo = mid;
        else
            hi = mid - 1;
    }
    return lo;
}
/** A square's key: `j * cols + i`. */
export const squareKey = (g, i, j) => j * g.cols + i;
export function squareCentre(g, q) {
    const i = q % g.cols;
    const j = Math.floor(q / g.cols);
    return { x: (g.xs[i] + g.xs[i + 1]) / 2, y: (g.ys[j] + g.ys[j + 1]) / 2 };
}
/** Whether segment a–b meets the closed rectangle (touching its edge counts). */
function segmentMeetsBox(a, b, x0, y0, x1, y1) {
    const inBox = (p) => p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1;
    if (inBox(a) || inBox(b))
        return true;
    const c = [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
    for (let k = 0; k < 4; k += 1)
        if (segmentsMeet(a, b, c[k], c[(k + 1) % 4]))
            return true;
    return false;
}
/**
 * The squares a wall borders or crosses: every square whose closed rectangle the segment touches. A wall along a grid
 * line borders the squares on both sides of it. Used to list walls by square, and for what a player is sent of a wall
 * (design/30 §4: a wall that borders or crosses an explored square).
 */
export function wallSquares(w, g) {
    if (!g.cols || !g.rows)
        return [];
    const i0 = lineCellOf(g.xs, Math.min(w.a.x, w.b.x));
    const i1 = lineCellOf(g.xs, Math.max(w.a.x, w.b.x));
    const j0 = lineCellOf(g.ys, Math.min(w.a.y, w.b.y));
    const j1 = lineCellOf(g.ys, Math.max(w.a.y, w.b.y));
    const out = [];
    for (let j = Math.max(0, j0 - 1); j <= Math.min(g.rows - 1, j1 + 1); j += 1) {
        for (let i = Math.max(0, i0 - 1); i <= Math.min(g.cols - 1, i1 + 1); i += 1) {
            if (segmentMeetsBox(w.a, w.b, g.xs[i], g.ys[j], g.xs[i + 1], g.ys[j + 1]))
                out.push(squareKey(g, i, j));
        }
    }
    return out;
}
export function indexWalls(walls, g) {
    const cells = Array.from({ length: g.cols * g.rows }, () => []);
    for (const w of walls) {
        if (!blocksSight(w))
            continue;
        for (const q of wallSquares(w, g))
            cells[q].push(w);
    }
    return { g, cells };
}
/**
 * Whether the line from `a` to `b` is clear of every sight-blocking wall, touching included. It walks the squares the
 * line passes through, from `a`'s to `b`'s, and tests the walls listed in each.
 */
export function lineClear(a, b, idx) {
    const { g, cells } = idx;
    if (!g.cols || !g.rows)
        return true;
    let i = lineCellOf(g.xs, a.x);
    let j = lineCellOf(g.ys, a.y);
    const bi = lineCellOf(g.xs, b.x);
    const bj = lineCellOf(g.ys, b.y);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const si = Math.sign(dx);
    const sj = Math.sign(dy);
    // The fraction of the line at which it crosses the next line on each axis.
    const nextX = (ci) => (si === 0 ? Infinity : ((si > 0 ? g.xs[ci + 1] : g.xs[ci]) - a.x) / dx);
    const nextY = (cj) => (sj === 0 ? Infinity : ((sj > 0 ? g.ys[cj + 1] : g.ys[cj]) - a.y) / dy);
    let tx = nextX(i);
    let ty = nextY(j);
    const tested = new Set();
    for (let steps = 0; steps <= g.cols + g.rows + 2; steps += 1) {
        for (const w of cells[squareKey(g, i, j)]) {
            if (tested.has(w))
                continue;
            tested.add(w);
            if (segmentsMeet(a, b, w.a, w.b))
                return false;
        }
        if (i === bi && j === bj)
            return true;
        if (tx <= ty) {
            i += si;
            tx = nextX(i);
        }
        else {
            j += sj;
            ty = nextY(j);
        }
        if (i < 0 || j < 0 || i >= g.cols || j >= g.rows)
            return true;
    }
    return true;
}
/** Every square whose centre is in the eye's line of sight, no range limit: what an eye sees in a bright or dim scene (a dark one: `litFrom`). */
export function seenFrom(eye, idx) {
    const out = new Set();
    const n = idx.g.cols * idx.g.rows;
    for (let q = 0; q < n; q += 1)
        if (lineClear(eye, squareCentre(idx.g, q), idx))
            out.add(q);
    return out;
}
/** A square's size in board pixels on each axis: the board's average pitch, so a fitted, uneven grid measures as its squares do. */
export function squareSize(g) {
    return { x: g.cols ? (g.xs[g.cols] - g.xs[0]) / g.cols : 1, y: g.rows ? (g.ys[g.rows] - g.ys[0]) / g.rows : 1 };
}
/**
 * The squares a light reaches (issue 1145; design/30 §1.4): each square whose centre is within `dim` squares of the
 * light and in its line (walls block light exactly as they block sight), mapped to whether it is within `bright`.
 * Only the squares inside the radius's box are tried, so a torch costs its own neighbourhood, not the board.
 */
export function litFrom(at, bright, dim, idx) {
    const g = idx.g;
    const out = new Map();
    if (!g.cols || !g.rows)
        return out;
    const u = squareSize(g);
    const i0 = lineCellOf(g.xs, at.x - dim * u.x);
    const i1 = lineCellOf(g.xs, at.x + dim * u.x);
    const j0 = lineCellOf(g.ys, at.y - dim * u.y);
    const j1 = lineCellOf(g.ys, at.y + dim * u.y);
    for (let j = j0; j <= j1; j += 1) {
        for (let i = i0; i <= i1; i += 1) {
            const q = squareKey(g, i, j);
            const c = squareCentre(g, q);
            const d = Math.hypot((c.x - at.x) / u.x, (c.y - at.y) / u.y);
            if (d <= dim && lineClear(at, c, idx))
                out.set(q, d <= bright);
        }
    }
    return out;
}
//# sourceMappingURL=los.js.map